import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useWeb3React } from '@web3-react/core';
import { useNavigation } from '../../contexts/NavigationContext';
import { useSolanaWallet } from '../../web3/connectors';
import DocumentationMegaMenu from './DocumentationMegaMenu';
import { truncateAddress } from '../../utils/addressUtils';
import './DesktopNavigation.css';

interface NavigationItem {
  name: string;
  href: string;
  isRoute: boolean;
  hasMegaMenu?: boolean;
}

interface DesktopNavigationProps {
  navigationItems: NavigationItem[];
  onWalletConnect?: () => void;
}

const DesktopNavigation: React.FC<DesktopNavigationProps> = ({
  navigationItems,
  onWalletConnect
}) => {
  const location = useLocation();
  const { isActive, account, connector } = useWeb3React();
  const { connected: solanaConnected, publicKey: solanaPublicKey, disconnect: disconnectSolana, connect: connectSolana } = useSolanaWallet();
  const { docsMenuOpen, setDocsMenuOpen } = useNavigation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [hoverTimeout, setHoverTimeout] = useState<NodeJS.Timeout | null>(null);
  const [showWalletMenu, setShowWalletMenu] = useState(false);
  const docsMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const walletMenuRef = useRef<HTMLDivElement>(null);

  // Determine which wallet is connected
  const isWalletConnected = isActive || solanaConnected;
  const connectedAddress = account || solanaPublicKey;
  const walletType = solanaConnected ? 'Solana' : isActive ? 'EVM' : null;
  
  // Check if MetaMask is installed (can switch between EVM and Solana)
  const isMetaMaskInstalled = typeof window !== 'undefined' && (window as any).ethereum?.isMetaMask;

  // Handle click outside to close wallet menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (walletMenuRef.current && !walletMenuRef.current.contains(event.target as Node)) {
        setShowWalletMenu(false);
      }
    };

    if (showWalletMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showWalletMenu]);

  const handleWalletClick = () => {
    console.log('Wallet button clicked', { isWalletConnected, isActive, solanaConnected, showWalletMenu });
    if (isWalletConnected) {
      setShowWalletMenu(!showWalletMenu);
    } else if (onWalletConnect) {
      onWalletConnect();
    }
  };

  const handleDisconnect = async () => {
    try {
      if (solanaConnected) {
        await disconnectSolana();
      } else if (isActive && connector) {
        // For EVM wallets, we need to properly reset the connection
        try {
          // Clear any stored connection data
          localStorage.removeItem('walletName');
          
          // Try to reset the connector state
          if ((connector as any).resetState) {
            await (connector as any).resetState();
          }
        } catch (evmError) {
          console.log('EVM disconnect method failed:', evmError);
        }
      }
      setShowWalletMenu(false);
    } catch (error) {
      console.error('Disconnect failed:', error);
    }
  };

  const handleSwitchNetwork = async () => {
    try {
      setShowWalletMenu(false);
      
      if (isActive && !solanaConnected) {
        // Currently on EVM, switch to Solana via MetaMask Snap
        // First disconnect EVM properly
        try {
          localStorage.removeItem('walletName');
          if ((connector as any).resetState) {
            await (connector as any).resetState();
          }
        } catch (e) {
          console.log('EVM disconnect error:', e);
        }
        
        // Small delay to ensure disconnect completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Then connect Solana
        await connectSolana('MetaMask Snap');
      } else if (solanaConnected && !isActive) {
        // Currently on Solana, switch to EVM
        // First disconnect Solana
        await disconnectSolana();
        
        // Small delay to ensure disconnect completes
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Open wallet modal to connect EVM wallet
        if (onWalletConnect) {
          onWalletConnect();
        }
      }
    } catch (error) {
      console.error('Network switch failed:', error);
    }
  };

  // Handle Documentation menu hover with delay
  const handleDocsHover = (isHovering: boolean) => {
    if (docsMenuTimeoutRef.current) {
      clearTimeout(docsMenuTimeoutRef.current);
    }

    if (isHovering) {
      // Show menu after 200ms delay (preloading)
      docsMenuTimeoutRef.current = setTimeout(() => {
        setDocsMenuOpen(true);
      }, 200);
    } else {
      // Hide menu after 300ms delay to allow mouse movement
      docsMenuTimeoutRef.current = setTimeout(() => {
        setDocsMenuOpen(false);
      }, 300);
    }
  };

  const handleMegaMenuHover = () => {
    // Keep menu open when hovering over mega menu
    if (docsMenuTimeoutRef.current) {
      clearTimeout(docsMenuTimeoutRef.current);
    }
  };

  const handleMegaMenuLeave = () => {
    // Close menu when leaving mega menu
    docsMenuTimeoutRef.current = setTimeout(() => {
      setDocsMenuOpen(false);
    }, 300);
  };

  const closeMegaMenu = () => {
    setDocsMenuOpen(false);
  };

  // Clean up timeouts
  useEffect(() => {
    return () => {
      if (docsMenuTimeoutRef.current) {
        clearTimeout(docsMenuTimeoutRef.current);
      }
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);

  const handleItemHover = (itemName: string) => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    
    const timeout = setTimeout(() => {
      setHoveredItem(itemName);
    }, 100);
    setHoverTimeout(timeout);
  };

  const handleItemLeave = () => {
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
    }
    setHoveredItem(null);
  };

  const isDocumentationItem = (item: NavigationItem) => {
    return item.name === 'Documentation';
  };

  const isActiveLink = (item: NavigationItem) => {
    if (item.isRoute) {
      if (item.href === '/') {
        return location.pathname === '/';
      }
      return location.pathname.startsWith(item.href);
    }
    return false;
  };

  return (
    <div className="desktop-navigation">
      <nav className="desktop-nav-menu">
        <ul className="nav-list">
          {navigationItems.map((item, index) => (
            <li 
              key={item.name} 
              className={`nav-item ${isDocumentationItem(item) ? 'has-mega-menu' : ''}`}
              onMouseEnter={() => {
                handleItemHover(item.name);
                if (isDocumentationItem(item)) {
                  handleDocsHover(true);
                }
              }}
              onMouseLeave={() => {
                handleItemLeave();
                if (isDocumentationItem(item)) {
                  handleDocsHover(false);
                }
              }}
            >
              {item.isRoute ? (
                <motion.div
                  className="nav-link-wrapper"
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to={item.href}
                    className={`nav-link ${isActiveLink(item) ? 'active' : ''} ${
                      hoveredItem === item.name ? 'hovered' : ''
                    }`}
                    aria-expanded={isDocumentationItem(item) ? docsMenuOpen : undefined}
                    aria-haspopup={isDocumentationItem(item) ? 'menu' : undefined}
                  >
                    <span className="nav-link-text">{item.name}</span>
                    {isDocumentationItem(item) && (
                      <motion.svg
                        className="dropdown-arrow"
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        animate={{ rotate: docsMenuOpen ? 180 : 0 }}
                        transition={{ duration: 0.2, ease: "easeOut" }}
                      >
                        <path
                          d="M3 4.5L6 7.5L9 4.5"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </motion.svg>
                    )}
                    <span className="nav-link-glow" />
                  </Link>
                </motion.div>
              ) : (
                <motion.a
                  href={item.href}
                  className={`nav-link ${hoveredItem === item.name ? 'hovered' : ''}`}
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.5, 
                    delay: index * 0.1,
                    ease: "easeOut"
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="nav-link-text">{item.name}</span>
                  <span className="nav-link-glow" />
                </motion.a>
              )}

              {/* Documentation Mega Menu */}
              {isDocumentationItem(item) && (
                <DocumentationMegaMenu
                  isOpen={docsMenuOpen}
                  onMouseEnter={handleMegaMenuHover}
                  onMouseLeave={handleMegaMenuLeave}
                  onClose={closeMegaMenu}
                />
              )}
            </li>
          ))}
        </ul>

        {/* Action Button */}
        <div className="nav-actions" ref={walletMenuRef} style={{ position: 'relative' }}>
          <button
            className={`launch-app-btn ${isWalletConnected ? 'connected' : ''}`}
            onClick={() => {
              console.log('CLICKED!', { isWalletConnected, showWalletMenu });
              handleWalletClick();
            }}
            style={{
              cursor: 'pointer',
              position: 'relative',
              zIndex: 100
            }}
            type="button"
          >
            <span className="btn-text">
              {isWalletConnected && connectedAddress ? truncateAddress(connectedAddress) : 'Launch App'}
            </span>
            {isWalletConnected && <span className="wallet-indicator" style={{ marginLeft: '8px' }}>▼</span>}
            <span className="btn-glow" />
          </button>

          {/* Wallet Dropdown Menu */}
          <AnimatePresence>
            {showWalletMenu && isWalletConnected && (
              <motion.div
                className="wallet-dropdown-menu"
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: 'absolute',
                  top: 'calc(100% + 8px)',
                  right: 0,
                  background: 'rgba(20, 20, 30, 0.95)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  padding: '8px',
                  minWidth: '200px',
                  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
                  zIndex: 1000
                }}
              >
                <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px' }}>
                    Connected Wallet
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
                    {truncateAddress(connectedAddress || '')}
                  </div>
                  {walletType && (
                    <div style={{ fontSize: '11px', color: '#ab9ff2', marginTop: '4px' }}>
                      {walletType} Network
                    </div>
                  )}
                </div>
                
                {/* Network Switch Option for MetaMask */}
                {isMetaMaskInstalled && (
                  <button
                    onClick={handleSwitchNetwork}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: 'transparent',
                      border: 'none',
                      color: '#ab9ff2',
                      fontSize: '14px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      textAlign: 'left',
                      borderRadius: '8px',
                      transition: 'background 0.2s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(171, 159, 242, 0.1)'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <span>🔄</span>
                    {isActive && !solanaConnected ? 'Switch to Solana' : 'Switch to EVM'}
                  </button>
                )}
                
                <button
                  onClick={handleDisconnect}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'transparent',
                    border: 'none',
                    color: '#ff6b6b',
                    fontSize: '14px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                    borderRadius: '8px',
                    transition: 'background 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 107, 107, 0.1)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                >
                  <span>🔌</span> Disconnect Wallet
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </nav>
    </div>
  );
};

export default DesktopNavigation;